import { Link } from "react-router-dom";
import { ArrowUpRight, FileDown } from "lucide-react";

const LOGO =
  "https://images.getbento.com/accounts/78540d47b961291fe22c29ae3eb8a893/media/images/12400Patrizias-Brush.png?w=600&auto=compress,format";

const options = [
  {
    testid: "hub-link-mozzarella-show",
    label: "Concept 01",
    title: "Mozzarella Show",
    desc: "Fresh mozzarella pulled by hand, live at the table — the tableside experience.",
    to: "/patrizias",
    img: "https://static.prod-images.emergentagent.com/jobs/6918af59-ab46-445b-a012-e513807d76c0/images/15eec68d6fbd0b4e12a8b442cca983121cae716376f3712ff50a17ad99b79109.jpeg",
  },
  {
    testid: "hub-link-pizzeria",
    label: "Concept 02",
    title: "Pizzeria Concept",
    desc: "Roman pinsa and live fire — the Phase 2 concept, with its own interactive model.",
    to: "/pizzeria?brand=patrizias",
    img: "https://images.unsplash.com/photo-1677175183792-e6a9b8b14305?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

const pdfs = [
  {
    testid: "hub-pdf-patrizias",
    label: "One-Pager 01",
    title: "Patrizia's Edition",
    desc: "The Live Mozzarella Experience on one elegant page — branded for Patrizia's, ready to forward.",
    href: "/patrizias-onepager.pdf",
    file: "Patrizias-Live-Mozzarella-Experience.pdf",
  },
  {
    testid: "hub-pdf-filata",
    label: "One-Pager 02",
    title: "White-Label Edition",
    desc: "The same concept, brand-neutral — for sharing with other partners and venues.",
    href: "/filata-onepager.pdf",
    file: "Filata-Live-Mozzarella-Experience.pdf",
  },
];

export default function Hub() {
  return (
    <div className="min-h-screen bg-wine text-bg flex flex-col" data-testid="hub-page">
      <div className="noise-overlay" aria-hidden="true" />

      <header className="relative z-10 max-w-[1200px] w-full mx-auto px-6 md:px-10 pt-16 md:pt-24 text-center">
        <img
          src={LOGO}
          alt="Patrizia's"
          className="h-20 md:h-28 w-auto mx-auto mb-8 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          data-testid="hub-logo"
        />
        <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-4">
          Private Presentation · For Giacomo
        </p>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.02]">
          Two concepts, <em className="italic">one table.</em>
        </h1>
      </header>

      <main className="relative z-10 flex-1 max-w-[1200px] w-full mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {options.map((o) => (
            <Link
              key={o.testid}
              data-testid={o.testid}
              to={o.to}
              className="group relative block overflow-hidden rounded-sm min-h-[360px] md:min-h-[520px] shadow-[0_30px_60px_-24px_rgba(0,0,0,0.6)]"
            >
              <img
                src={o.img}
                alt={o.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-wine via-wine/40 to-wine/10 group-hover:from-wine/95 transition-colors duration-700" />
              <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                <div className="flex items-start justify-between">
                  <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-terracotta mb-3">{o.label}</p>
                  <ArrowUpRight className="h-6 w-6 text-bg/70 group-hover:text-terracotta transition-colors duration-500" />
                </div>
                <h2 className="font-serif text-4xl md:text-6xl text-bg leading-none">{o.title}</h2>
                <p className="font-sans text-sm md:text-base text-bg/70 mt-4 max-w-sm leading-relaxed">{o.desc}</p>
                <span className="font-sans text-sm text-bg/90 mt-6 inline-flex items-center gap-2">
                  <span className="h-px w-8 bg-terracotta transition-[width] duration-500 group-hover:w-14" />
                  Open concept
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-14 md:mt-20">
          <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6 text-center">
            Downloadable Materials
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-testid="hub-pdf-grid">
            {pdfs.map((p) => (
              <a
                key={p.testid}
                data-testid={p.testid}
                href={p.href}
                download={p.file}
                className="group flex items-start gap-5 bg-bg/5 border border-bg/15 rounded-sm p-6 md:p-7 hover:border-terracotta/60 hover:bg-bg/10 transition-colors duration-300"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-terracotta/15 border border-terracotta/40">
                  <FileDown className="h-5 w-5 text-terracotta" strokeWidth={1.75} />
                </span>
                <span>
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-bg/50 block mb-1">{p.label} · PDF</span>
                  <span className="font-serif text-2xl md:text-3xl text-bg block leading-tight">{p.title}</span>
                  <span className="font-sans text-sm text-bg/60 block mt-2 leading-relaxed">{p.desc}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      </main>

      <footer className="relative z-10 max-w-[1200px] w-full mx-auto px-6 md:px-10 py-10 border-t border-bg/15 text-center">
        <p className="font-sans text-xs text-bg/40">Private presentation prepared for Patrizia&rsquo;s — not publicly linked.</p>
      </footer>
    </div>
  );
}
